/**
 * Componente optimizado para mostrar el contador de notificaciones.
 * Muestra las visitas recibidas y se actualiza en tiempo real.
 * Incluye navegación a la pantalla de actividad y feedback visual.
 * Memoizado para evitar renders innecesarios.
 */

import React, { useState, useEffect, memo, useCallback } from 'react';
import { FiEye } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useVisitCount } from '../../../hooks/useVisitCount';
import { useInteractionCounterRealtime } from '../../../hooks/useInteractionCounterRealtime';
import { useResetCountersMutation } from '../../../hooks/useInteractions';
import { useAuth } from '../../../hooks/useAuth';

function NotificationCounter() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Usar hook personalizado para obtener contador de visitas con TanStack Query
  const { data: visitCount = 0, isLoading: loading } = useVisitCount(user);
  
  // Hook para reiniciar contadores de interacciones
  const { mutate: resetCounters, isPending: isResetting } = useResetCountersMutation(user?.id);
  
  // Move console.log to useEffect to avoid logging on every render
  useEffect(() => {
    console.log('[NotificationButtonCounter] Hook actualizado - visitCount:', visitCount, 'loading:', loading, 'isResetting:', isResetting);
  }, [visitCount, loading, isResetting]);

  // Estado optimista: si se está reseteando, mostrar como si ya estuviera en 0
  const optimisticVisitCount = isResetting ? 0 : visitCount;

  // Estado para mostrar etiqueta descriptiva
  const [showLabel, setShowLabel] = useState(false);

  /**
   * Maneja la acción de presionar el botón de notificaciones.
   * Reinicia los contadores a 0, navega a la pantalla de actividad.
   */
  const handleNotificationPress = () => {
    console.log('[NotificationButtonCounter] Botón presionado - visitCount:', visitCount, 'isResetting:', isResetting);
    
    // Reiniciar contadores antes de navegar
    if (visitCount > 0) {
      console.log('[NotificationButtonCounter] Condición cumplida (visitCount > 0), reiniciando contadores');
      resetCounters();
    } else {
      console.log('[NotificationButtonCounter] Condición no cumplida (visitCount <= 0), no se reinician contadores');
    }
    
    console.log('[NotificationButtonCounter] Navegando a /interacciones');
    navigate('/interacciones');
  };

  /**
   * Renderiza el indicador de punto (badge) cuando hay notificaciones.
   * Muestra un punto rojo en la esquina superior derecha.
   */
  const renderNotificationDot = () => {
    if (optimisticVisitCount <= 0) return null;
    
    return (
      <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full" />
    );
  };

  /**
   * Renderiza el texto del contador con etiqueta condicional.
   * Muestra "Visita/Visitas" durante los primeros 2 segundos.
   */
  const renderCounterText = () => {
    // Determina el texto correcto según el número de visitas
    const visitText = optimisticVisitCount === 1 ? 'Visita' : 'Visitas';
    const labelText = showLabel && optimisticVisitCount > 0 ? `${visitText} ` : '';

    return (
      <span className="text-white text-base ml-2">
        {optimisticVisitCount} {labelText}
      </span>
    );
  };

  // Efecto para manejar la etiqueta descriptiva
  useEffect(() => {
    console.log('[NotificationButtonCounter] useEffect triggered - optimisticVisitCount:', optimisticVisitCount);
    
    // Mostrar la etiqueta cada vez que cambia el contador
    if (optimisticVisitCount > 0) {
      console.log('[NotificationButtonCounter] Mostrando etiqueta por 2 segundos');
      setShowLabel(true); // Mostrar etiqueta inmediatamente
      const timer = setTimeout(() => {
        console.log('[NotificationButtonCounter] Ocultando etiqueta después de 2 segundos');
        setShowLabel(false);
      }, 2000);
      return () => clearTimeout(timer);
    } else {
      console.log('[NotificationButtonCounter] optimisticVisitCount es 0, no mostrar etiqueta');
    }
  }, [optimisticVisitCount]);

  /**
   * Efecto que se ejecuta cuando la pantalla gana foco
   * Refresca las queries para asegurar datos actualizados al volver
   */
  // Nota: En React Router v6, useFocusEffect no está disponible nativamente
  // Se puede implementar usando useEffect con dependencias si es necesario

  /**
   * Callbacks memoizados para evitar recreación en cada render
   * Esto previene el ciclo de montaje/desmontaje en useInteractionCounterRealtime
   */
  const handleVisitasChange = useCallback((nuevoValor, valorAnterior) => {
    console.log(`[NotificationButtonCounter] Cambio en visitas: ${valorAnterior} → ${nuevoValor}`);
    // La invalidación de queries ya la maneja el hook automáticamente
    // No necesitamos hacer nada adicional aquí
  }, []);

  const handleCounterChange = useCallback((cambios, newData, oldData) => {
    console.log('[NotificationButtonCounter] Cambio en interaction_counter:', cambios);
    // Callback adicional si se necesita lógica específica para otros cambios
  }, []);

  /**
   * Usar hook específico para suscripciones en tiempo real a interaction_counter
   * Escucha cambios específicos en la columna de visitas
   * El hook se encarga de invalidar las queries necesarias automáticamente
   */
  useInteractionCounterRealtime({
    onVisitasChange: handleVisitasChange,
    onCounterChange: handleCounterChange
  });

  // No renderizar nada si está cargando o no hay visitas
  if (loading || optimisticVisitCount < 1) {
    return null;
  }

  return (
    <button 
      className="flex items-center px-4 py-2 gap-4"
      onClick={handleNotificationPress}
      aria-label={`Ver ${optimisticVisitCount} visitas`}
    >

      <div className="flex items-center relative ">
        {/* Icono del ojo para representar visitas */}
        <FiEye
          size={20}
          className="opacity-80"
          color="white"
        />
        
        {/* Texto del contador */}
        {renderCounterText()}
        
      </div>
      <span className="text-primary-500">Ver todo</span>
    </button>
  );
}

// Memoizar el componente para evitar renders innecesarios
export default memo(NotificationCounter);
